import useSWR from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher, endpoints } from "@/util/axios";

import type {
  Invoice,
  CardDetails,
  SubscriptionResponse,
  CheckoutSessionRequest,
  CheckSubscriptionRequest,
  UpdateSubscriptionRequest,
  CreateCustomerPortalRequest,
} from "./types";

// ----------------------------------------------------------------------
const PAYMENT_ENDPOINT = `${CONFIG.serverUrl}${endpoints.payment}`;

const ENDPOINTS = {
  getCardDetails: "get-card-details",
  getCustomerInvoices: "customer-invoices",
  createCheckoutSession: "create-checkout-session",
  createCustomerPortal: "create-customer-portal",
  checkSubscription: "check-subscription",
  cancelSubscription: "cancel-subscription",
  updateSubscription: "update-subscription",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export function useGetCustomerInvoices(
  workspaceId: string | null,
  isPlanTypeFree: boolean = true,
  canModifySettings: boolean = false
) {
  const url =
    workspaceId && canModifySettings && !isPlanTypeFree
      ? [
          `${PAYMENT_ENDPOINT}`,
          { params: { workspaceId, endpoint: ENDPOINTS.getCustomerInvoices } },
        ]
      : null;

  const { data, error, isLoading, isValidating } = useSWR<{
    invoices: Invoice[];
  }>(url, fetcher, swrOptions);

  return {
    stripeInvoices: data?.invoices || [],
    invoicesLoading: isLoading,
    invoicesError: error,
    invoicesValidating: isValidating,
    invoicesEmpty: !isLoading && !data?.invoices?.length,
  };
}

// ----------------------------------------------------------------------

export function useGetSubscription(customerId: string | null) {
  const url = customerId
    ? [
        `${PAYMENT_ENDPOINT}`,
        { params: { customerId, endpoint: ENDPOINTS.checkSubscription } },
      ]
    : null;

  const { data, error, isLoading, isValidating } = useSWR<{
    subscription: SubscriptionResponse;
  }>(url, fetcher, swrOptions);

  return {
    subscription: data?.subscription,
    subscriptionLoading: isLoading,
    subscriptionError: error,
    subscriptionValidating: isValidating,
  };
}

// ----------------------------------------------------------------------

export function useGetCardDetails(
  workspaceId: string,
  isPlanTypeFree: boolean = true,
  canModifySettings: boolean = false
) {
  const url =
    canModifySettings && !isPlanTypeFree
      ? [
          `${PAYMENT_ENDPOINT}`,
          { params: { workspaceId, endpoint: ENDPOINTS.getCardDetails } },
        ]
      : null;

  const { data, error, isLoading, isValidating } = useSWR<{
    cardDetails: CardDetails;
  }>(url, fetcher, swrOptions);

  return {
    cardDetails: data?.cardDetails,
    cardDetailsLoading: isLoading,
    cardDetailsError: error,
    cardDetailsValidating: isValidating,
  };
}

// ----------------------------------------------------------------------

export async function createCheckoutSession(request: CheckoutSessionRequest) {
  const params = { endpoint: ENDPOINTS.createCheckoutSession };
  const response = await axios.post(PAYMENT_ENDPOINT, request, { params });
  return response.data;
}

// ----------------------------------------------------------------------

export async function createCustomerPortal(
  request: CreateCustomerPortalRequest
) {
  const params = { endpoint: ENDPOINTS.createCustomerPortal };
  const response = await axios.post(PAYMENT_ENDPOINT, request, { params });
  return response.data;
}

// ----------------------------------------------------------------------

export async function checkSubscription(request: CheckSubscriptionRequest) {
  const params = { endpoint: ENDPOINTS.checkSubscription };
  const response = await axios.post(PAYMENT_ENDPOINT, request, { params });
  return response.data;
}

// ----------------------------------------------------------------------

export async function cancelSubscription(subscriptionId: string) {
  const params = { endpoint: ENDPOINTS.cancelSubscription };
  const response = await axios.post(
    PAYMENT_ENDPOINT,
    { subscriptionId },
    { params }
  );

  return response.data;
}

// ----------------------------------------------------------------------

export async function updateSubscription(request: UpdateSubscriptionRequest) {
  const params = { endpoint: ENDPOINTS.updateSubscription };
  const response = await axios.post(PAYMENT_ENDPOINT, request, { params });

  return response.data;
}

// ----------------------------------------------------------------------
